import React, { useEffect, useState, useRef, lazy } from 'react';
import { Button } from './ui/button';
import { CheckCircle, Play } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
interface HeroSectionProps {
  onNavigate?: (page: 'home' | 'fitur' | 'kontak') => void;
}
/** Counts up from 0 to target once the element is in view */
function CountUp({
  target,
  suffix = '',
  prefix = '',
  duration = 1800,
  className = ''
}: {
  target: number;
  suffix?: string;
  prefix?: string;
  duration?: number;
  className?: string;
}) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (now: number) => {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          // Ease out cubic
          const eased = 1 - Math.pow(1 - progress, 3);
          setValue(Math.round(eased * target));
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, {
      threshold: 0.5
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);
  return <div ref={ref} className={className} data-id="element-145">
      {prefix}
      {value}
      {suffix}
    </div>;
}
export function HeroSection({
  onNavigate
}: HeroSectionProps) {
  const {
    t
  } = useLanguage();
  return <>
      {/* Full-screen Hero */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden" data-id="element-146">
        {/* Background Image */}
        <div className="absolute inset-0" data-id="element-147">
          <img src="https://cdn.magicpatterns.com/uploads/gFhkKkPkJFJofmQM7c78mo/4074823996_c8fa98c562_o.jpg" alt="Pabrik kelapa sawit (PKS) — pemandangan udara fasilitas mill yang dikelola dengan software sawit-net" className="w-full h-full object-cover object-center" fetchPriority="high" decoding="async" loading="eager" data-id="element-148" />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-green/90 via-brand-green/70 to-forest-950/95" data-id="element-149" />
          <div className="absolute inset-0 bg-gradient-to-r from-forest-950/20 via-transparent to-forest-950/10" data-id="element-150" />
        </div>

        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff04_1px,transparent_1px),linear-gradient(to_bottom,#ffffff04_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" data-id="element-151" />

        {/* Floating ambient orbs */}
        <div className="absolute top-1/4 left-[10%] w-72 h-72 rounded-full bg-brand-gold/20 blur-3xl pointer-events-none animate-float-orb" style={{
        animationDelay: '0s'
      }} data-id="element-152" />
        <div className="absolute top-1/3 right-[8%] w-96 h-96 rounded-full bg-brand-green/30 blur-3xl pointer-events-none animate-float-orb-slow" style={{
        animationDelay: '2s'
      }} data-id="element-153" />
        <div className="absolute bottom-1/4 left-1/3 w-64 h-64 rounded-full bg-brand-gold/10 blur-2xl pointer-events-none animate-float-orb" style={{
        animationDelay: '4s'
      }} data-id="element-154" />

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 md:px-8 pt-32 pb-24 md:pt-40 md:pb-32" data-id="element-155">
          <div className="max-w-4xl mx-auto text-center" data-id="element-156">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm mb-8 shadow-lg animate-fade-in-up" style={{
            animationDelay: '0ms'
          }} data-id="element-157">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" data-id="element-158" />
              <span className="text-white/90 text-xs font-semibold tracking-widest uppercase" data-id="element-159">
                {t.hero.badge}
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-[1.08] mb-6 font-heading animate-fade-in-up" style={{
            animationDelay: '120ms'
          }} data-id="element-160">
              {t.hero.headline1} <br className="hidden md:block" data-id="element-161" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-300 to-brand-gold" data-id="element-162">
                {t.hero.headline2}
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-white/75 leading-relaxed mb-8 max-w-2xl mx-auto font-light animate-fade-in-up" style={{
            animationDelay: '240ms'
          }} data-id="element-163">
              {t.hero.subtitle}
            </p>

            {/* Trust signals */}
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-10 text-sm text-white/70 animate-fade-in-up" style={{
            animationDelay: '360ms'
          }} data-id="element-164">
              {[t.hero.trust1, t.hero.trust2, t.hero.trust3].map(item => <span key={item} className="flex items-center gap-1.5" data-id="element-165">
                  <CheckCircle className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-166" />
                  {item}
                </span>)}
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 md:mb-20 animate-fade-in-up" style={{
            animationDelay: '480ms'
          }} data-id="element-167">
              <Button onClick={() => {
              const el = document.getElementById('contact');
              if (el) el.scrollIntoView({
                behavior: 'smooth'
              });
            }} className="h-12 px-8 py-3.5 bg-brand-gold hover:bg-gold-400 text-white font-bold text-base rounded-xl w-full sm:w-auto transition-all shadow-xl shadow-brand-gold/25 hover:shadow-2xl hover:-translate-y-0.5" data-id="element-168">
                {t.hero.cta1}
              </Button>
              <Button variant="outline" onClick={() => onNavigate?.('fitur')} className="h-12 px-8 py-3.5 border-white/40 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:border-white/60 font-medium text-base rounded-xl w-full sm:w-auto transition-all" data-id="element-169">
                <Play className="w-4 h-4 mr-2 fill-white" data-id="element-170" />
                {t.hero.cta2}
              </Button>
            </div>

            {/* Stats Row — with CountUp */}
            <div className="grid grid-cols-3 gap-2 md:gap-4 max-w-2xl mx-auto animate-fade-in-up" style={{
            animationDelay: '600ms'
          }} data-id="element-171">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl md:rounded-2xl p-3 md:p-6 text-center" data-id="element-172">
                <CountUp target={20} suffix="%+" duration={1600} className="text-2xl md:text-4xl font-black text-white mb-1 tracking-tight" data-id="element-173" />
                <div className="text-[10px] md:text-sm text-white/60 font-medium leading-tight" data-id="element-174">
                  {t.hero.stat1Label}
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl md:rounded-2xl p-3 md:p-6 text-center" data-id="element-175">
                <CountUp target={100} suffix="%" duration={1800} className="text-2xl md:text-4xl font-black text-white mb-1 tracking-tight" data-id="element-176" />
                <div className="text-[10px] md:text-sm text-white/60 font-medium leading-tight" data-id="element-177">
                  {t.hero.stat2Label}
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl md:rounded-2xl p-3 md:p-6 text-center" data-id="element-178">
                <div className="text-2xl md:text-4xl font-black text-white mb-1 tracking-tight" data-id="element-179">
                  24/7
                </div>
                <div className="text-[10px] md:text-sm text-white/60 font-medium leading-tight" data-id="element-180">
                  {t.hero.stat3Label}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom fade to white */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none" data-id="element-181" />
      </section>

      {/* Dashboard Screenshot */}
      <section className="relative bg-white pb-16 md:pb-24" data-id="element-182">
        <div className="container mx-auto px-6 md:px-8" data-id="element-183">
          <div className="relative max-w-6xl mx-auto pt-4" data-id="element-184">
            {/* Gentle float on the whole card */}
            <div className="animate-float-y" style={{
            animationDuration: '6s'
          }} data-id="element-185">
              <div className="bg-gradient-to-b from-forest-100 to-transparent rounded-2xl p-px shadow-2xl shadow-forest-200/50" data-id="element-186">
                <div className="relative rounded-xl overflow-hidden border border-forest-800/30 bg-forest-950" data-id="element-187">
                  {/* Browser Chrome */}
                  <div className="h-10 bg-forest-950 border-b border-forest-800/50 flex items-center px-4 gap-2" data-id="element-188">
                    <div className="flex gap-1.5" data-id="element-189">
                      <div className="w-3 h-3 rounded-full bg-red-500/60" data-id="element-190" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500/60" data-id="element-191" />
                      <div className="w-3 h-3 rounded-full bg-green-500/60" data-id="element-192" />
                    </div>
                    <div className="ml-4 flex-1 flex justify-center" data-id="element-193">
                      <div className="bg-forest-900 rounded px-3 py-1 text-[10px] text-forest-400 font-mono flex items-center gap-1.5" data-id="element-194">
                        <span className="w-2 h-2 rounded-full bg-brand-gold/60 animate-pulse" data-id="element-195" />
                        app.sawit-net.com/dashboard
                      </div>
                    </div>
                  </div>
                  <img src="https://cdn.magicpatterns.com/uploads/rpHNegJhi9AbiPSYST2Gsx/Screenshot_2026-02-18_at_03-04-11_HT_Group_App.png" alt="Dashboard sawit-net — software pabrik kelapa sawit (PKS) terintegrasi dengan monitoring produksi real-time" className="w-full block" loading="lazy" decoding="async" data-id="element-196" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}